/**
 * Modal dialogs for the unified /calculate view.
 *
 * A single request and a batch of requests produce the *same* result rows
 * ("bulk is nothing than multiple single requests all together"), so both use
 * the same detail modal: click any result row to see every field of that
 * result and copy it as JSON. App-level alerts (HTTP failures, batch-validation
 * errors) use the alert modal — an overlay can never land below IxContent's
 * fold, which is why the modal is the reliable place to surface them.
 *
 * IX modals are shown imperatively via showModal({ content }); the content is
 * mounted in IX's own portal. We pass a Modal ref into the content so the
 * footer buttons and the header close (X) can dismiss it.
 */
import { createRef, useState } from 'react';
import {
  showModal,
  Modal,
  IxModalHeader,
  IxModalContent,
  IxModalFooter,
  IxButton,
  IxKeyValue,
  IxKeyValueList,
  IxMessageBar,
  IxTypography,
  type ModalRef,
} from '@siemens/ix-react';
import { iconCopy, iconCheck } from '@siemens/ix-icons/icons';
import type { GAPriceDto } from '../api/client.js';
import { brandButtonStyle } from '../styles/brandButton.js';

// Every GAPriceDto field, in display order, paired with a human label.
const DETAIL_FIELDS: ReadonlyArray<readonly [keyof GAPriceDto, string]> = [
  ['Id', 'Id'],
  ['GaPrice', 'GA Price'],
  ['Currency', 'Currency'],
  ['GaValidFrom', 'Valid From'],
  ['GaValidTo', 'Valid To'],
  ['ResponseStatus', 'Status'],
  ['ResponseMessage', 'Message'],
  ['Timestamp', 'Timestamp'],
];

function formatValue(v: string | null | undefined): string {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

// Copy-to-clipboard with a transient "Copied" acknowledgement on the button.
const COPY_ACK_MS = 1500;

function useCopy(text: string) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPY_ACK_MS);
    } catch {
      // The Clipboard API can be blocked (insecure context / denied permission);
      // there is nothing useful to do here, so fail quietly.
    }
  }
  return { copied, copy };
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const { copied, copy } = useCopy(text);
  return (
    <IxButton
      variant="secondary"
      icon={copied ? iconCheck : iconCopy}
      onClick={() => void copy()}
    >
      {copied ? 'Copied' : label}
    </IxButton>
  );
}

// ---------------------------------------------------------------------------
// Result detail
// ---------------------------------------------------------------------------

function ResultDetailModal({
  modalRef,
  dto,
  index,
  message,
}: {
  modalRef: React.RefObject<ModalRef | null>;
  dto: GAPriceDto;
  index: number;
  message: string | null;
}) {
  const json = JSON.stringify(dto, null, 2);
  return (
    <Modal ref={modalRef} centered>
      <IxModalHeader onCloseClick={() => modalRef.current?.dismiss()}>
        Result detail — Request ID {index + 1}
      </IxModalHeader>
      <IxModalContent>
        <div
          data-testid="result-detail"
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
        >
          {message && <IxMessageBar type="alarm">{message}</IxMessageBar>}
          <IxKeyValueList>
            {DETAIL_FIELDS.map(([key, label]) => (
              <IxKeyValue key={key} label={label} labelPosition="left" value={formatValue(dto[key])} />
            ))}
          </IxKeyValueList>
        </div>
      </IxModalContent>
      <IxModalFooter>
        <CopyButton text={json} label="Copy JSON" />
        <IxButton style={brandButtonStyle} onClick={() => modalRef.current?.close(true)}>Close</IxButton>
      </IxModalFooter>
    </Modal>
  );
}

/** Opens the detail modal for one result row (single or batch — identical). */
export function openResultDetail(dto: GAPriceDto, index: number, message: string | null): void {
  const modalRef = createRef<ModalRef>();
  // Fire-and-forget: showModal resolves when the dialog closes; we don't act on
  // that, and a .catch keeps the promise handled without swallowing it silently.
  showModal({
    centered: true,
    closeOnBackdropClick: true,
    content: <ResultDetailModal modalRef={modalRef} dto={dto} index={index} message={message} />,
  }).catch(() => {
    /* modal host unavailable — nothing actionable */
  });
}

// ---------------------------------------------------------------------------
// App-level alert (errors, validation)
// ---------------------------------------------------------------------------

function AlertModal({
  modalRef,
  title,
  message,
}: {
  modalRef: React.RefObject<ModalRef | null>;
  title: string;
  message: string;
}) {
  return (
    <Modal ref={modalRef} centered>
      <IxModalHeader onCloseClick={() => modalRef.current?.dismiss()}>{title}</IxModalHeader>
      <IxModalContent>
        <div data-testid="alert-modal" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <IxMessageBar type="alarm">{message}</IxMessageBar>
          <IxTypography format="body-sm" color="soft">
            You can copy the message for a bug report, then close this dialog and try again.
          </IxTypography>
        </div>
      </IxModalContent>
      <IxModalFooter>
        <CopyButton text={message} label="Copy" />
        <IxButton style={brandButtonStyle} onClick={() => modalRef.current?.close(true)}>Close</IxButton>
      </IxModalFooter>
    </Modal>
  );
}

/** Opens an alert modal — used for HTTP failures and batch-validation errors. */
export function openAlert(title: string, message: string): void {
  const modalRef = createRef<ModalRef>();
  showModal({
    centered: true,
    closeOnBackdropClick: true,
    content: <AlertModal modalRef={modalRef} title={title} message={message} />,
  }).catch(() => {
    /* modal host unavailable — nothing actionable */
  });
}
